import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import usersService from '~/services/usersService/users.service';
import { UpdateProfileRequestDTO } from '@bitecritic-api/src/users/dto/user.dto';
import { ACCESS_TOKEN } from '~/constants';
import { ROUTES } from '~/constants/routes.contants';
import { formatHref } from '~/components/atoms/Link/Link';
import { useLocale } from '~/hooks/useLocale';
import { getCookie, setCookie } from '~/hooks/useCookie';
import { UserPrivateProfileResponseDto } from '@bitecritic-api/src/users/dto/user.response.dto';
interface UserContextProps {
  user: UserPrivateProfileResponseDto | null;
  token: string | null;
  loading: boolean;
  setUser: (user: UserPrivateProfileResponseDto | null) => void;
  storeToken: (token: string) => Promise<UserPrivateProfileResponseDto | null>;
  logout: () => Promise<void>;
  error: unknown;
  updateAvatar: (imageId: string) => Promise<void>;
  updateUserData: (data: UpdateProfileRequestDTO) => Promise<void>;
  updateUserOnClient: (data: Partial<UserPrivateProfileResponseDto>) => void;
  isAuthenticated: boolean;
  addLikedReview: (reviewId: string) => void;
  removeLikedReview: (reviewId: string) => void;
  hasLikedReview: (reviewId: string) => boolean;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserPrivateProfileResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { currentLocale, nextRouter } = useLocale();

  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      let existingToken = getTokenFromLocalStorage();

      if (!existingToken) {
        existingToken = getTokenFromCookie();
      }

      if (existingToken) {
        setToken(existingToken);
      } else {
        setLoading(false);
      }
    }
  }, [token, setToken]);

  useEffect(() => {
    if (user && !user.username && !nextRouter.pathname.includes(ROUTES.ONBOARDING)) {
      nextRouter.push(formatHref(currentLocale.code, ROUTES.ONBOARDING));
    }
  }, [user, nextRouter, currentLocale]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userData = await usersService.getProfile();
      setUser(userData);
      return userData;
    } catch (err: unknown) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setCookie(ACCESS_TOKEN, '', '/', 0);
    localStorage.removeItem(ACCESS_TOKEN);
    setToken(null);
    setUser(null);
  }

  const storeToken = async (token: string) => {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN, token);
      setCookie(ACCESS_TOKEN, token, '/', 365);
      setToken(token);
      return await fetchUserProfile();
    } else {
      console.error('No token provided');
      return null;
    }
  }

  const getTokenFromLocalStorage = () => {
    return localStorage.getItem(ACCESS_TOKEN);
  }

  const getTokenFromCookie = () => {
    return getCookie(ACCESS_TOKEN);
  }

  const updateAvatar = async (imageId: string) => {
    try {
      const updatedUser = await usersService.updateProfile({ profilePictureUrl: imageId });
      setUser(updatedUser);
    } catch (err: unknown) {
      setError(err);
      throw err;
    }
  };

  const updateUserData = async (data: UpdateProfileRequestDTO) => {
    try {
      await usersService.updateProfile(data);
      await fetchUserProfile();
    } catch (err: unknown) {
      setError(err);
      throw err;
    }
  };

  const updateUserOnClient = (data: Partial<UserPrivateProfileResponseDto>) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return {
        ...prevUser,
        ...data
      };
    });
  };

  const addLikedReview = useCallback((reviewId: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        likedReviewIdList: [...(prev.likedReviewIdList || []), reviewId],
      };
    });
  }, []);

  const removeLikedReview = useCallback((reviewId: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        likedReviewIdList: (prev.likedReviewIdList || []).filter((id: string) => id !== reviewId),
      };
    });
  }, []);

  const hasLikedReview = useCallback(
    (reviewId: string) => {
      return user?.likedReviewIdList?.includes(reviewId) || false;
    },
    [user?.likedReviewIdList],
  );

  return (
    <UserContext.Provider value={{
      user,
      token,
      setUser,
      loading,
      error,
      logout,
      updateAvatar,
      updateUserData,
      updateUserOnClient,
      isAuthenticated: !!user,
      addLikedReview,
      removeLikedReview,
      hasLikedReview,
      storeToken,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
